/**
 * FINN-side cancel on the Interviews log — the surface for cancellations that have NO inbound
 * email behind them (a role closes, an interviewer leaves), which the Candidate Requests card can
 * never carry. Removing the old "Manage" link (the impersonation workaround) without this would
 * have left staff no in-app way to cancel those at all, and two of the staff reasons would have
 * been vocabulary no surface could send.
 *
 * Two-step on purpose, with a mandatory reason: this button emails a candidate. No 10-second undo
 * here (that machinery is the action board's); the confirm step is the pause, and the server's
 * atomic claim is — as everywhere — the actual guarantee that a double click sends one notice.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { cancelInterviewBooking, STAFF_CANCEL_REASONS } from '../services/interviewsApi';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

export function InterviewCancelButton({
  bookingRef,
  candidateName,
  onCancelled,
  surface = 'log',
}: {
  bookingRef: string;
  candidateName: string | null;
  /** The list re-fetches, so the row flips to cancelled with real server state. */
  onCancelled: () => void;
  /** Where the button sits — the Log or a request card — so the two are separable in analytics. */
  surface?: 'log' | 'requests';
}) {
  const [open, setOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState<string>('role_closed');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const result = await cancelInterviewBooking(bookingRef, { reasonCode, note: note || undefined });
      if (result.alreadyCancelled) {
        void logAnalyticsEvent('interview_cancelled_by_staff_duplicate', { surface });
        showToast({ status: 'info', description: 'This interview was already cancelled.' });
        onCancelled();
        return;
      }
      if (!result.cancelled) throw new Error('The interview was not cancelled — try again.');
      // Reason CODE and booleans only — never the note text or the candidate (PII rule).
      void logAnalyticsEvent('interview_cancelled_by_staff', {
        surface,
        reason_code: reasonCode,
        with_note: note.trim().length > 0,
        email_sent: Boolean(result.emailSent),
      });
      showToast({
        status: 'success',
        description: `${candidateName || 'The candidate'}'s interview is cancelled${
          result.emailSent ? ' — they have been emailed' : ''
        }.`,
      });
      onCancelled();
    } catch (e) {
      void logAnalyticsEvent('interview_cancelled_by_staff_failed', { surface, reason_code: reasonCode });
      showToast({
        status: 'error',
        description: e instanceof Error ? e.message : 'Could not cancel the interview.',
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Cancel…
      </Button>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Select value={reasonCode} onValueChange={setReasonCode} disabled={busy}>
        <SelectTrigger aria-label="Cancellation reason" className="text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STAFF_CANCEL_REASONS.map((r) => (
            <SelectItem key={r.code} value={r.code}>
              {r.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {reasonCode === 'other' && (
        <Input
          value={note}
          onChange={(e) => setNote((e.target as HTMLInputElement).value)}
          disabled={busy}
          placeholder="Briefly, why?"
          aria-label="Cancellation note"
          className="w-36"
        />
      )}
      <Button
        size="sm"
        variant="primary"
        onClick={() => void confirm()}
        disabled={busy || (reasonCode === 'other' && note.trim().length < 3)}
      >
        {busy ? 'Cancelling…' : 'Cancel & email them'}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
        Keep it
      </Button>
    </span>
  );
}
